import { Survey, PrimarySurvey } from '../../../Surveys/util';
import SurveyResponse from '../../../Surveys/util/SurveyResponse';
import { useSurveys } from '../../../../hooks/useSurveys';
import { useSurveyResponses } from '../../../../hooks/useSurveyResponses';
import { useMemo, useState } from 'react';
import SurveyResponseResults, {
  TotalResult,
} from '../../../Surveys/util/SurveyResponseResults';
import { useCustomCompareEffect } from 'react-use';
import { useSurveyTemplates } from '../../../Surveys/util/useSurveyTemplates';
import { SurveyTemplateWithId } from '../../../../services/SurveyService';

export type SurveyResultsFilters = {
  hideDisplayOnlyContent: boolean;
  showFavorites: boolean;
};

export type UseSurveyResultsReturnValue = {
  primarySurveys: PrimarySurvey[];
  selectableResponses: SurveyResponse[] | null;
  isLoading: boolean;
  results: TotalResult;
  selectedPrimarySurveyId: string | null;
  selectedResponseId: string | null;
  setPrimarySurveyId: (primarySurveyId: string) => void;
  setResponseId: (responseId: string) => void;
  hasScoring: boolean;
  author: string;
  selectableTemplates: SurveyTemplateWithId[];
  selectedTemplate: SurveyTemplateWithId | null;
  setSurveyTemplate: (template: SurveyTemplateWithId | null) => void;
  selectedSurveyId: string | null;
  filters: SurveyResultsFilters;
  onFiltersChange: (filters: SurveyResultsFilters) => void;
  updateResponse: (response: fhir.QuestionnaireResponse) => void;
};

function getSelectableResponses(
  primarySurveyId: string,
  primarySurveys: PrimarySurvey[],
  questionnaireResponses: fhir.QuestionnaireResponse[],
) {
  if (!primarySurveyId) return null;
  const targetPrimarySurvey = primarySurveys.find(
    _ => _.primarySurveyId === primarySurveyId,
  );

  const primarySurveyQuestionnaireIds = new Set(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    targetPrimarySurvey.questionnaireIds,
  );

  return (
    (questionnaireResponses || [])
      .map(questionnaireResponse => {
        const sourceQuestionnaire = SurveyResponse.getSourceQuestionnaire(
          questionnaireResponse,
        );
        if (
          !sourceQuestionnaire ||
          !primarySurveyQuestionnaireIds.has(sourceQuestionnaire)
        )
          return null;

        const sourceSurvey =
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          targetPrimarySurvey.getTargetSurvey(sourceQuestionnaire);
        return SurveyResponse.initFromFhirQuestionnaireResponse(
          questionnaireResponse,
          sourceSurvey.allRepeatingItems,
        );
      })
      .filter(Boolean)
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      .filter(_ => !!_.authoredDate)
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      .sort((r1, r2) => r2.authoredDate.getTime() - r1.authoredDate.getTime())
  );
}

function useSurveyResults(patientId: string): UseSurveyResultsReturnValue {
  const {
    isFetchingResponses,
    responses: questionnaireResponses,
    updateResponse,
  } = useSurveyResponses({
    patientId,
    status: 'completed',
  });
  const { isFetchingSurveys, surveys: questionnaires } = useSurveys(
    undefined,
    true,
    (questionnaireResponses
      ?.map(response => response.questionnaire?.reference?.split('/')?.at(-1))
      .filter(Boolean) as string[]) || [],
  );

  const primarySurveys = useMemo<PrimarySurvey[]>(() => {
    const allResponseQuestionnaireIds = (questionnaireResponses || [])
      .map(questionnaireResponse =>
        questionnaireResponse?.questionnaire?.reference?.split('/')?.at(-1),
      )
      .filter(Boolean);
    return PrimarySurvey.initFromAllQuestionnaires(questionnaires || []).filter(
      primarySurvey => {
        const questionnaireIdSet = new Set(primarySurvey.questionnaireIds);
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return allResponseQuestionnaireIds.find(_ => questionnaireIdSet.has(_));
      },
    );
  }, [questionnaireResponses, questionnaires]);
  const [selectedPrimarySurveyId, setSelectedPrimarySurveyId] =
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    useState<string>(null);
  const selectableResponses = useMemo(() => {
    return getSelectableResponses(
      selectedPrimarySurveyId,
      primarySurveys,
      questionnaireResponses,
    );
  }, [primarySurveys, questionnaireResponses, selectedPrimarySurveyId]);
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const [selectedResponseId, setSelectedResponseId] = useState<string>(null);
  const [filters, setFilters] = useState<SurveyResultsFilters>({
    hideDisplayOnlyContent: true,
    showFavorites: false,
  });
  const [selectedSurveyTemplate, setSelectedSurveyTemplate] =
    useState<SurveyTemplateWithId | null>(null);
  const surveyResponseResults = useMemo<SurveyResponseResults | null>(() => {
    if (!selectedResponseId || !selectedPrimarySurveyId) return null;

    const targetQuestionnaireResponse = (questionnaireResponses || []).find(
      _ => _.id === selectedResponseId,
    );

    if (!targetQuestionnaireResponse) {
      console.error(
        `Unable to find cached questionnaire response with ID: ${selectedResponseId}`,
      );
      return null;
    }

    const sourceQuestionnaire = (questionnaires || []).find(
      _ =>
        _.id ===
        targetQuestionnaireResponse?.questionnaire?.reference
          ?.split('/')
          ?.at(-1),
    );

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const asSurvey = new Survey(sourceQuestionnaire);
    const asSurveyResponse = SurveyResponse.initFromFhirQuestionnaireResponse(
      targetQuestionnaireResponse,
      asSurvey.allRepeatingItems,
    );

    return new SurveyResponseResults(asSurvey, asSurveyResponse, {
      excludeDisplayItems: filters.hideDisplayOnlyContent,
      excludeNonFavorites: filters.showFavorites,
    });
  }, [
    questionnaireResponses,
    questionnaires,
    selectedPrimarySurveyId,
    selectedResponseId,
    filters,
  ]);

  const results = useMemo<TotalResult | null>(
    () => surveyResponseResults?.results || null,
    [surveyResponseResults],
  );
  const hasScoring = useMemo<boolean>(
    () => !!surveyResponseResults?.hasScoring,
    [surveyResponseResults],
  );
  const author = useMemo<string | null>(
    () => surveyResponseResults?.author || null,
    [surveyResponseResults],
  );

  // default the selection upon load
  const isLoading = isFetchingSurveys || isFetchingResponses;
  useCustomCompareEffect(
    () => {
      if (isLoading) return;

      const firstPrimarySurveyId = primarySurveys?.[0]?.primarySurveyId;
      const defaultResponse = getSelectableResponses(
        firstPrimarySurveyId,
        primarySurveys,
        questionnaireResponses,
      )?.[0]?.id;
      if (firstPrimarySurveyId && defaultResponse) {
        setSelectedResponseId(defaultResponse);
        setSelectedPrimarySurveyId(firstPrimarySurveyId);
      }
    },
    [isLoading, primarySurveys, questionnaireResponses],
    ([prevIsLoading], [curIsLoading]) => prevIsLoading === curIsLoading,
  );

  const selectedSurveyId = useMemo(() => {
    if (!selectedResponseId || !selectableResponses) return null;

    const targetResponse = selectableResponses.find(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      _ => _.id === selectedResponseId,
    );

    if (!targetResponse) return null;

    return targetResponse.sourceQuestionnaireId;
  }, [selectedResponseId, selectableResponses]);
  const surveyTemplates = useSurveyTemplates({
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    surveyId: selectedSurveyId,
  });

  return {
    isLoading: isFetchingSurveys || isFetchingResponses,
    primarySurveys,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    selectableResponses,
    selectedPrimarySurveyId,
    selectedResponseId,
    setPrimarySurveyId: (primarSurveyId: string) => {
      const selectableResponses = getSelectableResponses(
        primarSurveyId,
        primarySurveys,
        questionnaireResponses,
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setSelectedResponseId(selectableResponses?.[0]?.id || null);
      setSelectedPrimarySurveyId(primarSurveyId);
      setSelectedSurveyTemplate(null);
    },
    setResponseId: setSelectedResponseId,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    results,
    hasScoring,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    author,
    selectableTemplates: surveyTemplates?.surveyTemplates || [],
    selectedTemplate: selectedSurveyTemplate,
    setSurveyTemplate: setSelectedSurveyTemplate,
    selectedSurveyId,
    filters,
    onFiltersChange: setFilters,
    updateResponse,
  };
}

export default useSurveyResults;
